/**
 * Rich Document Editor for AEP Consolidado (per CNPJ) — Fase 1 MVP.
 *
 * Flow:
 *   1. Load `/aep-document` → if exists, opens with saved HTML.
 *   2. If empty, imports a fresh HTML snapshot from the current AEP report
 *      (rendered off-screen with `renderAEPSection`) as the initial content.
 *   3. User edits with TipTap toolbar (bold/italic/lists/tables/images/etc).
 *   4. Autosave onBlur + manual "Salvar rascunho".
 *   5. "Modo Visualização" hides the toolbar & renders as final layout.
 *   6. "Gerar PDF final" opens window.print() with @page A4 config.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Image from "@tiptap/extension-image";
import FontFamily from "@tiptap/extension-font-family";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";

import api from "@/lib/api";

export default function CnpjAEPDocumentEditor() {
  const { cnpj } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const importedRef = useRef(false);
  // Phase 3 — Header / Footer / TOC
  const [headerHtml, setHeaderHtml] = useState("");
  const [footerHtml, setFooterHtml] = useState("");
  const [showHeaderFooter, setShowHeaderFooter] = useState(false);
  // Phase 2 — versions
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [autosaveOn, setAutosaveOn] = useState(true);
  const dirtyRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow, TableHeader, TableCell,
      Image.configure({ inline: false, allowBase64: true }),
      TextStyle, FontFamily, Color,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "aep-document-editor prose max-w-none focus:outline-none min-h-[297mm] p-[20mm] bg-white shadow-md",
        "data-testid": "aep-doc-editor-body",
      },
      // Phase 3: paste image from clipboard as base64
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (!file) continue;
            const reader = new FileReader();
            reader.onload = () => {
              const src = reader.result;
              const { state, dispatch } = view;
              const node = state.schema.nodes.image?.create({ src });
              if (node) dispatch(state.tr.replaceSelectionWith(node));
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: () => { dirtyRef.current = true; },
  });

  // -------- Load existing document + versions ------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [docRes, verRes] = await Promise.all([
          api.get(`/cnpj/${cnpj}/aep-document`),
          api.get(`/cnpj/${cnpj}/aep-document/versions`).catch(() => ({ data: [] })),
        ]);
        if (!alive || !editor) return;
        if (docRes.data && docRes.data.content_html) {
          editor.commands.setContent(docRes.data.content_html);
          setStatus(docRes.data.status || "draft");
          setVersion(docRes.data.version || 0);
          setUpdatedAt(docRes.data.updated_at || null);
          setHeaderHtml(docRes.data.header_html || "");
          setFooterHtml(docRes.data.footer_html || "");
          dirtyRef.current = false;
        }
        setVersions(verRes.data || []);
      } catch (_) { /* start with empty */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [cnpj, editor]);

  // -------- Import visual snapshot from AEP report -------------------------
  // Rationale: TipTap's schema strips SVG/canvas/custom-CSS backgrounds,
  // which erases charts, matrix diagrams, cover backgrounds and QR codes.
  // To preserve visual fidelity, we rasterize each `page-break` block from
  // the live AEP report into a PNG via html2canvas and insert them as
  // `<img>` nodes into the editor. Text/typography around the visuals stays
  // fully editable through TipTap's paragraph/heading/table primitives.
  const importSnapshot = useCallback(async () => {
    if (!editor) return;
    if (!window.confirm("Substituir o conteúdo do editor pelo AEP atual? A edição atual será perdida.")) return;
    setLoading(true);
    const t = toast.loading("Renderizando AEP em alta resolução…");
    let iframe = null;
    try {
      iframe = document.createElement("iframe");
      // Fixed A4-like width so section widths match the print layout.
      iframe.style.cssText = "position:fixed;left:-9999px;top:0;width:1240px;height:2000px;visibility:hidden;border:0;background:#fff;";
      iframe.src = `/aep/cnpj/${cnpj}`;
      document.body.appendChild(iframe);
      await new Promise((resolve) => {
        const done = () => resolve();
        iframe.onload = () => setTimeout(done, 5000); // let React sections fetch + render
        setTimeout(done, 10000);
      });

      const doc = iframe.contentDocument;
      const body = doc?.querySelector(".aep-body") || doc?.body;
      if (!body) {
        toast.error("Não foi possível ler o conteúdo do AEP.", { id: t });
        return;
      }
      // Strip interactive-only elements before snapshotting
      body.querySelectorAll(".no-print, button, input, select, textarea, script").forEach((el) => el.remove());

      // Split into visual pages. Every `.page-break` in the AEP is a printable
      // page; snapshot each one independently for reasonable PNG sizes.
      const sections = Array.from(body.querySelectorAll(".page-break"));
      const targets = sections.length ? sections : [body];

      const parts = [];
      for (let i = 0; i < targets.length; i++) {
        toast.loading(`Capturando página ${i + 1}/${targets.length}…`, { id: t });
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(targets[i], {
          backgroundColor: "#ffffff",
          scale: 1.75,            // print-friendly resolution (kept below MongoDB 16MB doc cap)
          useCORS: true,
          allowTaint: false,
          logging: false,
          windowWidth: 1240,
          onclone: (clonedDoc) => {
            clonedDoc.documentElement.style.fontFamily = "Inter, system-ui, sans-serif";
          },
        });
        // JPEG @ 0.85 is ~5-8x smaller than PNG for gradient/photo-heavy AEP
        // pages while remaining visually equivalent at print DPI.
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        parts.push(`<p><img src="${dataUrl}" alt="Página ${i + 1} do AEP" style="max-width:100%;display:block;margin:0 auto 24px;" /></p>`);
      }

      editor.commands.setContent(parts.join("\n"));
      toast.success(`AEP importado (${targets.length} página${targets.length > 1 ? "s" : ""}).`, { id: t });
    } catch (e) {
      toast.error(`Falha ao importar AEP: ${e.message || e}`, { id: t });
    } finally {
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
      setLoading(false);
    }
  }, [cnpj, editor]);

  // -------- Auto-import when empty on first load ---------------------------
  useEffect(() => {
    if (loading || !editor || importedRef.current) return;
    const html = editor.getHTML();
    if (!html || html === "<p></p>") {
      importedRef.current = true;
      // Don't auto-confirm — surface a call-to-action banner instead
    }
  }, [loading, editor]);

  // -------- Save ------------------------------------------------------------
  const save = useCallback(async (newStatus, opts = {}) => {
    if (!editor) return;
    if (opts.silent) { /* used by autosave — no toast */ }
    setSaving(true);
    try {
      const r = await api.put(`/cnpj/${cnpj}/aep-document`, {
        content_html: editor.getHTML(),
        status: newStatus || status,
        header_html: headerHtml,
        footer_html: footerHtml,
      });
      setStatus(r.data.status || "draft");
      setVersion(r.data.version || 0);
      setUpdatedAt(r.data.updated_at || null);
      dirtyRef.current = false;
      if (!opts.silent) {
        if (newStatus) toast.success(`Documento salvo como "${newStatus}".`);
        else toast.success("Rascunho salvo.");
      }
    } catch (e) {
      if (!opts.silent) toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }, [cnpj, editor, status, headerHtml, footerHtml]);

  // -------- Phase 2: Autosave every 20s if dirty ---------------------------
  useEffect(() => {
    if (!autosaveOn || !editor) return;
    const id = setInterval(() => {
      if (dirtyRef.current && !saving) save(null, { silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, [autosaveOn, editor, save, saving]);

  // -------- Phase 2: Snapshot current state as a named version ------------
  const snapshotVersion = useCallback(async () => {
    if (!editor) return;
    const label = window.prompt("Nome desta versão (ex.: Revisão pré-cliente)", `Snapshot ${new Date().toLocaleString("pt-BR")}`);
    if (!label) return;
    try {
      const r = await api.post(`/cnpj/${cnpj}/aep-document/versions`, {
        label,
        content_html: editor.getHTML(),
        header_html: headerHtml,
        footer_html: footerHtml,
      });
      setVersions((prev) => [r.data, ...prev]);
      toast.success("Versão salva no histórico.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [cnpj, editor, headerHtml, footerHtml]);

  const restoreVersion = useCallback(async (vid) => {
    if (!editor) return;
    if (!window.confirm("Restaurar esta versão? O conteúdo atual será substituído (você pode salvar como snapshot antes).")) return;
    try {
      const r = await api.get(`/cnpj/${cnpj}/aep-document/versions/${vid}`);
      editor.commands.setContent(r.data.content_html || "");
      setHeaderHtml(r.data.header_html || "");
      setFooterHtml(r.data.footer_html || "");
      dirtyRef.current = true;
      toast.success(`Versão "${r.data.label}" restaurada. Clique em Salvar para aplicar.`);
      setShowVersions(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [cnpj, editor]);

  const deleteVersion = useCallback(async (vid) => {
    if (!window.confirm("Excluir esta versão do histórico?")) return;
    try {
      await api.delete(`/cnpj/${cnpj}/aep-document/versions/${vid}`);
      setVersions((prev) => prev.filter((v) => v.id !== vid));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [cnpj]);

  // -------- Phase 3: Insert TOC (Sumário automático) -----------------------
  const insertToc = useCallback(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const items = [];
    doc.querySelectorAll("h1, h2, h3").forEach((h, idx) => {
      const level = parseInt(h.tagName.substring(1), 10);
      const text = (h.textContent || "").trim();
      if (text) items.push({ level, text, idx });
    });
    if (items.length === 0) {
      toast.error("Nenhum título (H1/H2/H3) encontrado para gerar sumário.");
      return;
    }
    const listHtml = `<h2>Sumário</h2><ul style="list-style:none;padding-left:0;">
      ${items.map((it) => `<li style="padding-left:${(it.level - 1) * 16}px;font-weight:${it.level === 1 ? 700 : 500};">
        ${it.level === 1 ? "▸ " : ""}${it.text.replace(/</g, "&lt;")}
      </li>`).join("")}
    </ul><hr/>`;
    editor.chain().focus().insertContentAt(0, listHtml).run();
    toast.success(`Sumário inserido no início com ${items.length} itens.`);
  }, [editor]);

  // -------- Image upload (base64) ------------------------------------------
  const insertImage = useCallback(() => {
    if (!editor) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => editor.chain().focus().setImage({ src: reader.result }).run();
      reader.readAsDataURL(file);
    };
    input.click();
  }, [editor]);

  const insertTable = () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const triggerPrint = () => setTimeout(() => window.print(), 60);

  if (!editor) return <div className="p-8 text-slate-500">Iniciando editor…</div>;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="aep-doc-editor-root">
      {/* Print styles + header/footer running elements */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .aep-doc-editor-canvas { box-shadow: none !important; padding: 0 !important; }
          .aep-document-editor { box-shadow: none !important; margin: 0 !important; }
          /* Running header/footer: fixed at top/bottom, browser repeats them
             on every printed page (Chrome behaviour with @page + fixed). */
          .aep-doc-page-header {
            position: fixed; top: 0; left: 0; right: 0;
            padding: 5mm 18mm; font-size: 9pt; color: #475569;
            border-bottom: 1px solid #cbd5e1; background: #fff;
            z-index: 100;
          }
          .aep-doc-page-footer {
            position: fixed; bottom: 0; left: 0; right: 0;
            padding: 5mm 18mm; font-size: 9pt; color: #475569;
            border-top: 1px solid #cbd5e1; background: #fff;
            display: flex; justify-content: space-between; align-items: center;
            z-index: 100;
          }
          .aep-doc-page-footer::after { content: "Página " counter(page) " / " counter(pages); }
        }
        @page { size: A4 portrait; margin: 25mm 18mm 22mm; }
        .aep-document-editor { width: 210mm; margin: 24px auto; }
        .aep-document-editor h1 { font-size: 24pt; font-weight: 800; margin: 16px 0 8px; }
        .aep-document-editor h2 { font-size: 18pt; font-weight: 700; margin: 14px 0 6px; }
        .aep-document-editor h3 { font-size: 14pt; font-weight: 700; margin: 12px 0 4px; }
        .aep-document-editor table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        .aep-document-editor table td, .aep-document-editor table th { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 10pt; }
        .aep-document-editor table th { background: #f1f5f9; font-weight: 700; }
        .aep-document-editor img { max-width: 100%; height: auto; }
        .aep-document-editor .page-break { page-break-before: always; break-before: page; }
      `}</style>

      {/* Fixed running header/footer for print — hidden on screen */}
      {(headerHtml || footerHtml) && (
        <>
          <div className="aep-doc-page-header hidden print:block" dangerouslySetInnerHTML={{ __html: headerHtml }} />
          <div className="aep-doc-page-footer hidden print:flex" dangerouslySetInnerHTML={{ __html: footerHtml }} />
        </>
      )}

      {/* Top toolbar */}
      <div className="no-print sticky top-0 z-40 bg-slate-900 text-white">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to={`/aep/cnpj/${cnpj}`} className="text-emerald-300 hover:text-emerald-200 text-sm" data-testid="aep-doc-back">
            <i className="fa-solid fa-arrow-left mr-1"></i> Voltar ao AEP
          </Link>
          <div className="text-sm font-bold tracking-wide">Editor do Documento · CNPJ {cnpj}</div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${status === "final" ? "bg-emerald-600" : status === "revised" ? "bg-amber-500 text-slate-900" : "bg-slate-600"}`} data-testid="aep-doc-status">
            {status === "final" ? "Finalizado" : status === "revised" ? "Revisado" : "Rascunho"} · v{version}
          </span>
          {updatedAt && (
            <span className="text-[10px] text-slate-400" title={updatedAt}>Salvo {new Date(updatedAt).toLocaleString("pt-BR")}</span>
          )}
          <div className="flex-1"></div>
          <div className="flex items-center gap-1 bg-slate-800 rounded p-0.5">
            <button onClick={() => setMode("edit")} className={`text-xs px-3 py-1 rounded ${mode === "edit" ? "bg-emerald-600" : ""}`} data-testid="aep-doc-mode-edit">Edição</button>
            <button onClick={() => setMode("preview")} className={`text-xs px-3 py-1 rounded ${mode === "preview" ? "bg-emerald-600" : ""}`} data-testid="aep-doc-mode-preview">Visualização</button>
          </div>
          <button onClick={importSnapshot} className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded" data-testid="aep-doc-import">
            <i className="fa-solid fa-file-import mr-1"></i> Importar AEP
          </button>
          <button onClick={snapshotVersion} className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded" data-testid="aep-doc-snapshot" title="Salvar versão atual no histórico">
            <i className="fa-solid fa-clone mr-1"></i> Duplicar / Snapshot
          </button>
          <button onClick={() => setShowVersions((v) => !v)} className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded relative" data-testid="aep-doc-versions">
            <i className="fa-solid fa-clock-rotate-left mr-1"></i> Versões
            {versions.length > 0 && <span className="ml-1 inline-block bg-emerald-600 text-[10px] font-bold px-1.5 rounded-full">{versions.length}</span>}
          </button>
          <button onClick={() => setShowHeaderFooter((v) => !v)} className={`text-xs px-3 py-1.5 rounded ${showHeaderFooter ? "bg-emerald-600" : "bg-slate-700 hover:bg-slate-600"}`} data-testid="aep-doc-headerfooter">
            <i className="fa-solid fa-heading mr-1"></i> Cabeçalho / Rodapé
          </button>
          <label className="text-[11px] text-slate-300 flex items-center gap-1" title="Autosave a cada 20s enquanto houver mudanças">
            <input type="checkbox" checked={autosaveOn} onChange={(e) => setAutosaveOn(e.target.checked)} data-testid="aep-doc-autosave" />
            Autosave
          </label>
          <button onClick={() => save("draft")} disabled={saving} className="text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-1.5 rounded font-bold" data-testid="aep-doc-save-draft">
            <i className="fa-solid fa-floppy-disk mr-1"></i> {saving ? "Salvando…" : "Salvar rascunho"}
          </button>
          <button onClick={() => save("final")} disabled={saving} className="text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 rounded font-bold" data-testid="aep-doc-save-final">
            <i className="fa-solid fa-circle-check mr-1"></i> Marcar como final
          </button>
          <button onClick={triggerPrint} className="text-xs bg-white text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded font-bold" data-testid="aep-doc-print">
            <i className="fa-solid fa-print mr-1"></i> Gerar PDF final
          </button>
        </div>

        {/* Rich toolbar (only in edit mode) */}
        {mode === "edit" && (
          <div className="bg-slate-800 border-t border-slate-700 px-4 py-2 flex items-center gap-1 flex-wrap text-white text-xs" data-testid="aep-doc-toolbar">
            <ToolBtn testid="tb-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito"><b>B</b></ToolBtn>
            <ToolBtn testid="tb-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico"><i>I</i></ToolBtn>
            <ToolBtn testid="tb-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sublinhado"><u>S</u></ToolBtn>
            <ToolBtn testid="tb-strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Riscado"><s>S</s></ToolBtn>
            <ToolSep />
            {[1, 2, 3, 4].map((lvl) => (
              <ToolBtn key={lvl} testid={`tb-h${lvl}`} active={editor.isActive("heading", { level: lvl })} onClick={() => editor.chain().focus().toggleHeading({ level: lvl }).run()} title={`Título ${lvl}`}>H{lvl}</ToolBtn>
            ))}
            <ToolBtn testid="tb-p" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} title="Parágrafo">¶</ToolBtn>
            <ToolSep />
            <ToolBtn testid="tb-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista"><i className="fa-solid fa-list-ul"></i></ToolBtn>
            <ToolBtn testid="tb-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numerada"><i className="fa-solid fa-list-ol"></i></ToolBtn>
            <ToolBtn testid="tb-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citação"><i className="fa-solid fa-quote-right"></i></ToolBtn>
            <ToolSep />
            <ToolBtn testid="tb-al-l" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Esquerda"><i className="fa-solid fa-align-left"></i></ToolBtn>
            <ToolBtn testid="tb-al-c" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centralizar"><i className="fa-solid fa-align-center"></i></ToolBtn>
            <ToolBtn testid="tb-al-r" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Direita"><i className="fa-solid fa-align-right"></i></ToolBtn>
            <ToolBtn testid="tb-al-j" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()} title="Justificar"><i className="fa-solid fa-align-justify"></i></ToolBtn>
            <ToolSep />
            <ToolBtn testid="tb-table" onClick={insertTable} title="Inserir tabela"><i className="fa-solid fa-table"></i></ToolBtn>
            <ToolBtn testid="tb-image" onClick={insertImage} title="Inserir imagem"><i className="fa-solid fa-image"></i></ToolBtn>
            <ToolBtn testid="tb-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linha"><i className="fa-solid fa-minus"></i></ToolBtn>
            <ToolBtn testid="tb-pgbrk" onClick={() => editor.chain().focus().insertContent('<div class="page-break"></div>').run()} title="Quebra de página"><i className="fa-solid fa-page-break"></i>⇥</ToolBtn>
            <ToolBtn testid="tb-toc" onClick={insertToc} title="Sumário automático (a partir de H1/H2/H3)"><i className="fa-solid fa-list"></i>Σ</ToolBtn>
            <ToolSep />
            <select
              onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
              defaultValue=""
              className="bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-[11px]"
              data-testid="tb-font"
            >
              <option value="">Fonte</option>
              <option value="Inter, sans-serif">Inter</option>
              <option value="Manrope, sans-serif">Manrope</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="Times New Roman, serif">Times</option>
              <option value="Courier New, monospace">Courier</option>
            </select>
            <input type="color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} className="w-6 h-6 bg-transparent border border-slate-700 rounded" title="Cor" data-testid="tb-color" />
            <ToolSep />
            <ToolBtn testid="tb-undo" onClick={() => editor.chain().focus().undo().run()} title="Desfazer"><i className="fa-solid fa-rotate-left"></i></ToolBtn>
            <ToolBtn testid="tb-redo" onClick={() => editor.chain().focus().redo().run()} title="Refazer"><i className="fa-solid fa-rotate-right"></i></ToolBtn>
          </div>
        )}
      </div>

      {/* Phase 3: Header/Footer edit panel */}
      {showHeaderFooter && mode === "edit" && (
        <div className="no-print bg-white border-b border-slate-200 px-6 py-3 shadow-sm" data-testid="aep-doc-hf-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-bold block mb-1">
                Cabeçalho (aparece no topo de cada página impressa)
              </label>
              <textarea
                value={headerHtml}
                onChange={(e) => { setHeaderHtml(e.target.value); dirtyRef.current = true; }}
                placeholder="Ex.: Sollute Ocupacional · AEP Consolidado · CNPJ 00.000.000/0001-00"
                rows={2}
                className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-emerald-400"
                data-testid="aep-doc-header-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-bold block mb-1">
                Rodapé (aparece no rodapé de cada página; a numeração é adicionada automaticamente à direita)
              </label>
              <textarea
                value={footerHtml}
                onChange={(e) => { setFooterHtml(e.target.value); dirtyRef.current = true; }}
                placeholder="Ex.: Documento confidencial — Cliente XYZ · versão 2026-06-30"
                rows={2}
                className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-emerald-400"
                data-testid="aep-doc-footer-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 max-w-5xl mx-auto">
            Aceita HTML simples (ex.: <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>). Só aparece na impressão/PDF, não no editor.
          </p>
        </div>
      )}

      {/* Phase 2: Versions dropdown */}
      {showVersions && (
        <div className="no-print bg-white border-b border-slate-200 px-6 py-3 shadow-sm max-h-[300px] overflow-y-auto" data-testid="aep-doc-versions-panel">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-slate-900">Histórico de versões ({versions.length})</div>
              <button onClick={() => setShowVersions(false)} className="text-xs text-slate-500 hover:text-slate-800">
                <i className="fa-solid fa-xmark"></i> fechar
              </button>
            </div>
            {versions.length === 0 ? (
              <p className="text-xs text-slate-500 italic py-3">Nenhuma versão salva ainda. Clique em &quot;Duplicar / Snapshot&quot; para criar uma.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {versions.map((v) => (
                  <li key={v.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`aep-doc-ver-${v.id}`}>
                    <i className="fa-solid fa-file-lines text-slate-400"></i>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{v.label}</div>
                      <div className="text-[11px] text-slate-500">
                        {new Date(v.created_at).toLocaleString("pt-BR")} · por {v.created_by_name || v.created_by}
                      </div>
                    </div>
                    <button onClick={() => restoreVersion(v.id)} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1 rounded font-semibold" data-testid={`aep-doc-restore-${v.id}`}>
                      <i className="fa-solid fa-rotate-left mr-1"></i> Restaurar
                    </button>
                    <button onClick={() => deleteVersion(v.id)} className="text-xs text-rose-600 hover:text-rose-800" data-testid={`aep-doc-delete-${v.id}`}>
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="aep-doc-editor-canvas py-6" data-testid="aep-doc-canvas">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function ToolBtn({ children, active, onClick, title, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`px-2 py-1 rounded hover:bg-slate-700 transition text-xs min-w-[28px] ${active ? "bg-emerald-600" : "bg-transparent"}`}
    >
      {children}
    </button>
  );
}
function ToolSep() { return <span className="mx-1 h-4 w-px bg-slate-600 inline-block"></span>; }
